import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { Play, Pause, RotateCcw, Volume2 } from 'lucide-preact';
import type { Story, WordTiming } from '../lib/types';
import { HEROES, SETTINGS, COLOR_CLASSES } from '../lib/constants';

interface StoryReaderProps {
  story: Story;
}

interface WordSpan {
  word: string;
  timing?: WordTiming;
  isPunctuation: boolean;
}

function parseContentToWords(content: string, wordTimings?: WordTiming[]): WordSpan[] {
  const words: WordSpan[] = [];
  const rawWords = content.split(/(\s+)/);
  let timingIndex = 0;

  for (const segment of rawWords) {
    if (/^\s+$/.test(segment)) {
      // Preserve whitespace
      words.push({ word: segment, isPunctuation: false });
    } else if (segment) {
      // Match word with timing (strip punctuation for matching)
      const cleanWord = segment.replace(/[.,!?;:'"()[\]{}—–-]/g, '');
      let timing: WordTiming | undefined;

      if (wordTimings && timingIndex < wordTimings.length) {
        const expectedWord = wordTimings[timingIndex]?.word.replace(/[.,!?;:'"()[\]{}—–-]/g, '');
        if (cleanWord.toLowerCase() === expectedWord?.toLowerCase()) {
          timing = wordTimings[timingIndex];
          timingIndex++;
        }
      }

      words.push({ word: segment, timing, isPunctuation: false });
    }
  }

  return words;
}

export function StoryReader({ story }: StoryReaderProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [activeWordIndex, setActiveWordIndex] = useState(-1);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const animationRef = useRef<number | null>(null);

  const hasAudio = !!story.audio?.url;
  const paragraphs = story.content.split('\n\n').filter((p) => p.trim());

  // Parse all words with timings
  const allWords = parseContentToWords(story.content, story.audio?.wordTimings);

  // Find illustration for a paragraph
  const getIllustration = (paragraphIndex: number) => {
    return story.illustrations?.find((ill) => ill.paragraphIndex === paragraphIndex);
  };

  // Update active word based on current playback time
  const updateActiveWord = useCallback(() => {
    if (!story.audio?.wordTimings || !audioRef.current) return;

    const time = audioRef.current.currentTime * 1000; // Convert to ms
    setCurrentTime(time);

    // Find the word that should be highlighted
    let wordIdx = 0;
    for (let i = 0; i < allWords.length; i++) {
      const wordSpan = allWords[i];
      if (wordSpan.timing) {
        if (time >= wordSpan.timing.start && time < wordSpan.timing.end) {
          setActiveWordIndex(wordIdx);
          break;
        } else if (time < wordSpan.timing.start) {
          break;
        }
        wordIdx++;
      }
    }

    if (isPlaying) {
      animationRef.current = requestAnimationFrame(updateActiveWord);
    }
  }, [allWords, isPlaying, story.audio?.wordTimings]);

  useEffect(() => {
    if (isPlaying) {
      animationRef.current = requestAnimationFrame(updateActiveWord);
    }
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, updateActiveWord]);

  const handlePlay = () => {
    if (!audioRef.current) return;

    if (audioRef.current.paused) {
      audioRef.current.play();
      setIsPlaying(true);
    } else {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleRestart = () => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = 0;
    setActiveWordIndex(-1);
    setCurrentTime(0);
    audioRef.current.play();
    setIsPlaying(true);
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setActiveWordIndex(-1);
  };

  // Track which word index we're at across paragraphs
  let globalWordIndex = 0;

  // Get hero and setting info
  const heroData = HEROES.find((h) => h.value === story.choices.animal);
  const settingData = SETTINGS.find((s) => s.value === story.choices.setting);
  const heroColors = heroData ? COLOR_CLASSES[heroData.color] : null;

  return (
    <div className="story-reader">
      {/* Hero introduction card */}
      {heroData && (
        <div className="flex items-center gap-4 mb-8 p-4 sm:p-5 rounded-2xl border border-gold/15 bg-parchment/30">
          {/* Hero icon */}
          <div
            className={`flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center ${heroColors?.bg || 'bg-parchment'}`}
          >
            <heroData.icon size={28} className={heroColors?.text || 'text-ink/60'} strokeWidth={1.5} />
          </div>
          {/* Hero info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="font-display text-lg sm:text-xl text-ink font-medium">
                {story.choices.childName}
              </span>
              <span className="text-ink/30">the</span>
              <span className={`font-medium ${heroColors?.text || 'text-ink/70'}`}>
                {heroData.label.split(' ')[0]} {heroData.label.split(' ').slice(1).join(' ')}
              </span>
            </div>
            {settingData && (
              <p className="text-sm text-ink/50">
                An adventure in the {settingData.label.toLowerCase()}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Audio element (hidden) */}
      {hasAudio && (
        <audio ref={audioRef} src={story.audio!.url} onEnded={handleEnded} preload="auto" />
      )}

      {/* Premium Audio controls */}
      {hasAudio && (
        <div className="flex flex-col items-center gap-4 mb-10 p-6 bg-parchment/50 rounded-2xl border border-gold/10">
          <div className="flex items-center gap-2 text-sm text-ink/50">
            <Volume2 size={16} />
            <span>Professional Narration</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handlePlay}
              className="flex items-center gap-2.5 px-8 py-3.5 bg-rust text-cream rounded-2xl font-medium text-lg
                       shadow-story hover:shadow-story-hover hover:-translate-y-0.5 transition-all cursor-pointer"
            >
              {isPlaying ? (
                <>
                  <Pause size={22} />
                  Pause
                </>
              ) : (
                <>
                  <Play size={22} fill="currentColor" />
                  {currentTime > 0 ? 'Resume' : 'Listen'}
                </>
              )}
            </button>
            {currentTime > 0 && (
              <button
                onClick={handleRestart}
                className="p-3.5 text-ink/40 hover:text-ink/70 hover:bg-cream rounded-xl transition-all cursor-pointer"
                title="Start over"
              >
                <RotateCcw size={20} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Story content with word highlighting */}
      <div className="prose-story">
        {paragraphs.map((paragraph, paragraphIdx) => {
          const illustration = getIllustration(paragraphIdx);
          const paragraphWords = parseContentToWords(paragraph, story.audio?.wordTimings);

          // Calculate word indices for this paragraph
          const paragraphStartIndex = globalWordIndex;
          const wordsWithTiming = paragraphWords.filter((w) => w.timing);

          const renderedParagraph = (
            <p
              key={paragraphIdx}
              className="animate-paragraph"
              style={{ animationDelay: `${paragraphIdx * 0.15}s` }}
            >
              {paragraphWords.map((wordSpan, idx) => {
                if (/^\s+$/.test(wordSpan.word)) {
                  return wordSpan.word;
                }

                // Check if this word should be highlighted
                const wordTimingIdx = paragraphWords
                  .slice(0, idx + 1)
                  .filter((w) => w.timing).length - 1;
                const globalIdx = paragraphStartIndex + wordTimingIdx;
                const isActive = hasAudio && wordSpan.timing && globalIdx === activeWordIndex;

                return (
                  <span
                    key={idx}
                    className={`transition-all duration-150 ${
                      isActive
                        ? 'bg-gold/40 text-ink rounded px-0.5 scale-105 inline-block'
                        : ''
                    }`}
                  >
                    {wordSpan.word}
                  </span>
                );
              })}
            </p>
          );

          // Update global word index
          globalWordIndex += wordsWithTiming.length;

          // Render illustration if present - premium treatment
          if (illustration) {
            return (
              <div key={`para-${paragraphIdx}`}>
                {paragraphIdx > 0 && renderedParagraph}
                <figure
                  className="my-10 sm:my-12 animate-paragraph relative"
                  style={{ animationDelay: `${paragraphIdx * 0.15 + 0.1}s` }}
                >
                  {/* Decorative frame effect */}
                  <div className="relative p-3 sm:p-4 bg-parchment rounded-3xl border border-gold/20 shadow-story">
                    {/* Corner accents */}
                    <div className="absolute top-2 left-2 w-4 h-4 border-l-2 border-t-2 border-gold/30 rounded-tl" />
                    <div className="absolute top-2 right-2 w-4 h-4 border-r-2 border-t-2 border-gold/30 rounded-tr" />
                    <div className="absolute bottom-2 left-2 w-4 h-4 border-l-2 border-b-2 border-gold/30 rounded-bl" />
                    <div className="absolute bottom-2 right-2 w-4 h-4 border-r-2 border-b-2 border-gold/30 rounded-br" />

                    <img
                      src={illustration.url}
                      alt={illustration.caption}
                      className="w-full rounded-2xl"
                      loading="lazy"
                    />
                  </div>
                  <figcaption className="text-center text-sm text-ink/40 mt-4 font-display italic px-4">
                    {illustration.caption}
                  </figcaption>
                </figure>
                {paragraphIdx === 0 && renderedParagraph}
              </div>
            );
          }

          return renderedParagraph;
        })}
      </div>

      {/* Premium Progress indicator */}
      {hasAudio && story.audio && (currentTime > 0 || isPlaying) && (
        <div className="mt-10 p-4 bg-parchment/30 rounded-2xl border border-gold/10">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="h-2 bg-gold/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-gold/60 to-gold rounded-full transition-all duration-100"
                  style={{ width: `${(currentTime / story.audio.duration) * 100}%` }}
                />
              </div>
            </div>
            <div className="text-xs text-ink/40 tabular-nums whitespace-nowrap">
              {Math.floor(currentTime / 60000)}:{String(Math.floor((currentTime / 1000) % 60)).padStart(2, '0')}
              {' / '}
              {Math.floor(story.audio.duration / 60000)}:{String(Math.floor((story.audio.duration / 1000) % 60)).padStart(2, '0')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
